import React, { Component } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { observer } from "mobx-react";
import "@uppy/core/dist/style.css";
import "@uppy/status-bar/dist/style.css";
import "whatwg-fetch";

@observer
class DragAndDropUploader extends Component {
  constructor(props) {
    super(props);

    // used with other variables to tell if files are being dragged into the media area. This is non trivial
    // because we can't tell if what's being dragged is an external file or a media item. Since media items are
    // not supposed to be uploaded, we'll need this variable (draggedStarted) to track if the dragging started
    // within the media list (then we know the dragged element is not uploadable).
    this.dragStarted = false;

    this.state = {
      draggingFiles: false,
    };
  }

  onDragStart() {
    // Keep track of drag start so we can later infer if the drag event started within the web page or outside.
    // If dragging happens outside, then the drag start won't fire. We'll assume this scenario happens when
    // user drags files from elsewhere from his computer into the droppable area. When drag start is registered
    // we'll assume it's because the dragging happens on a media item card (aka SortableMedia).
    this.dragStarted = true;
  }

  onDragEnter(event) {
    event.preventDefault();
    // Drag start always trigger first than drag enter. If drag started is true, then it means the dragging
    // triggered inside the droppable area, which means that what's being dragged is a media item, not an
    // uploadable file.
    if (!this.dragStarted) {
      const { draggingFiles } = this.state;

      if (!draggingFiles) {
        this.setState({ draggingFiles: true });
      }
    }
  }

  onDragEnd() {
    this.dragStarted = false;
    this.setState({ draggingFiles: false });
  }

  onDrop(event) {
    event.preventDefault();

    const { onFileReady } = this.props;
    const { files } = event.dataTransfer;

    // If dragStarted is true, it means we just drag and drop existing files
    // We do not want to upload the small 666x500 thumbnails as new uploaded images
    if (this.dragStarted) {
      return;
    }

    this.dragStarted = false;
    this.setState({ draggingFiles: false });

    if (!files || files.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      // get a pointer url to the blob.
      const url = URL.createObjectURL(file);

      // download the content (blob) from the file url.
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          onFileReady(file, blob);
        });
    }
  }

  onDragOver(event) {
    // Prevent the browser to e.g try to open the file instead of letting us have access to it for uploading.
    event.preventDefault();
  }

  render() {
    const { as: ElementType, children, onFileReady, disabled, ...rest } = this.props;
    const { draggingFiles } = this.state;

    if (disabled) {
      return <ElementType {...rest}>{children}</ElementType>;
    }

    return (
      <ElementType
        className={cx({ "dragging-files": draggingFiles })}
        // onDragLeave would have been really helpful to turn the highlight off,
        // but the media items seems to interfere with the drag events, the 'leave' event will
        // trigger many times while dragging media items inside this container, not only when actually leaving it.
        onDragStart={event => this.onDragStart(event)}
        onDragEnter={event => this.onDragEnter(event)}
        onDragEnd={event => this.onDragEnd(event)}
        onDrop={event => this.onDrop(event)}
        onDragOver={event => this.onDragOver(event)}
        {...rest}
      >
        {children}
      </ElementType>
    );
  }
}

DragAndDropUploader.propTypes = {
  as: PropTypes.elementType,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onFileReady: PropTypes.func.isRequired,
};

DragAndDropUploader.defaultProps = {
  as: "div",
  disabled: false,
};

export default DragAndDropUploader;
